
import type {
  ApiDashboardElement,
  ApiReturnCampaign,
  SourceLabelScope,
  SourceMap,
  WidgetEntity
} from '@highimpact-frontend/api-types';

import type { SourceLabelsApi } from '../api/DashboardHostApi';
import type { DashboardHostContextValue } from '../context/DashboardHostContext.types';
import type { PlotFormProps } from '../dashboard/widget/widgets/shared/types';
import { useCampaignsQuery } from '../query/useCampaignsQuery';
import {
  elementNeedsSourceLabelResolution,
  getMeasurePointPathsFromElement,
} from '../state/hydrationSourceLabels';
import { EMPTY_SOURCE_MAP } from '../state/resolveSourceLabels';

/** Props for widget forms that fetch their own scoped source candidates. */
export type MigratedPlotFormProps = PlotFormProps;

export function buildWidgetEntityFromHost(
  host: DashboardHostContextValue
): WidgetEntity | null {
  const project_id = host.project_id ?? host.project?.id ?? null;
  const site_id = host.site_id ?? host.site?.id ?? null;

  if (project_id == null || site_id == null) {
    return null;
  }

  const coordinates = host.site?.position?.coordinates;
  const longitude = coordinates?.[0] ?? 0;
  const latitude = coordinates?.[1] ?? 0;

  return {
    project_id,
    site_id,
    project_name: host.project?.name ?? `Project ${project_id}`,
    site_name: host.site?.name ?? `Site ${site_id}`,
    timezone: host.site?.timezone ?? null,
    latitude,
    longitude,
  };
}

/**
 * Clones a dashboard element for form editing so Immer-frozen Redux objects
 * are never passed to child components that spread or extend them.
 */
export function cloneDashboardElementForForm<T extends ApiDashboardElement>(
  element: T
): T {
  return structuredClone(element);
}

/**
 * Reads site-scoped campaigns from TanStack Query.
 */
export function useCampaignsForSite(
  siteId: number | null
): ApiReturnCampaign[] {
  const { campaigns } = useCampaignsQuery(siteId);
  return [...campaigns];
}

export function pickMigratedFormProps(
  formProps: PlotFormProps
): MigratedPlotFormProps {
  return formProps;
}

/**
 * Resolves label-map inputs for {@link addWidgetSafely} via the labels API.
 */
export async function fetchSourceLabelMapForElement(
  element: ApiDashboardElement,
  getLabels: SourceLabelsApi['getLabels'],
  scope?: SourceLabelScope
): Promise<SourceMap> {
  if (!elementNeedsSourceLabelResolution(element)) {
    return EMPTY_SOURCE_MAP;
  }

  const pairs = getMeasurePointPathsFromElement(element);

  if (pairs.length === 0) {
    return EMPTY_SOURCE_MAP;
  }

  return getLabels(pairs, scope);
}
